import {
  type Functions,
  httpsCallable,
  type HttpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions';

/**
 * Default timeout for every dashboard callable, in milliseconds.
 *
 * Regression context: during the 2026-05 QA pass several owner-facing mutations
 * (`patchStoreUserEntitlement`, the Stripe Connect "Begin payment setup" flow, etc.)
 * were observed to hang indefinitely with the action button stuck on its loading
 * label and no toast feedback. The Firebase JS SDK does not impose a client-side
 * timeout by default — if the callable never resolves, the awaiting mutation never
 * settles and the dashboard's existing error toast pipeline never fires.
 *
 * Capping every callable at 30 s lets the existing
 * `getCallableErrorMessage(err)` / toast plumbing surface the failure to the user
 * instead of leaving them staring at a frozen button.
 */
export const DEFAULT_HTTPS_CALLABLE_TIMEOUT_MS = 30_000;

/**
 * Wrapper around Firebase's `httpsCallable` that applies a default client-side timeout.
 *
 * Per-call overrides are honoured (e.g. long-running file uploads can opt into a
 * longer timeout by passing `{ timeout: 60_000 }`). Callers that need an
 * effectively-unbounded request should pass `{ timeout: 0 }` *and* document why.
 */
export function httpsCallableWithTimeout<RequestData = unknown, ResponseData = unknown>(
  functions: Functions,
  name: string,
  options?: HttpsCallableOptions,
): HttpsCallable<RequestData, ResponseData> {
  return httpsCallable<RequestData, ResponseData>(functions, name, {
    timeout: DEFAULT_HTTPS_CALLABLE_TIMEOUT_MS,
    ...options,
  });
}
